import { Link } from "react-router-dom";
import { User, ShieldCheck, SwatchBook, ListChecks, LifeBuoy, Sparkles, ChevronRight } from "lucide-react";

/**
 * Account home (KLA-039). The enterprise-grade front door to
 * everything account-shaped: one calm page, grouped the way a person
 * thinks, every card leading to a real working screen. Nothing here
 * is decorative and no control is a placebo; if a setting does not
 * exist yet, it is not shown.
 */

const GROUPS = [
  {
    label: "you",
    items: [
      {
        to: "/account/profile", icon: User, tint: "hsl(255 50% 96%)", iconColor: "hsl(255 45% 55%)",
        title: "Profile", body: "Your name, photo, and the goals Klair steers by.",
        tid: "account-card-profile",
      },
      {
        to: "/account/answers", icon: ListChecks, tint: "hsl(172 55% 94%)", iconColor: "hsl(var(--klareet-teal))",
        title: "Your answers", body: "Everything you told Klair, viewable and editable, with every change kept on record.",
        tid: "account-card-answers",
      },
    ],
  },
  {
    label: "account",
    items: [
      {
        to: "/account/security", icon: ShieldCheck, tint: "hsl(219 70% 95%)", iconColor: "hsl(var(--klareet-blue))",
        title: "Sign-in and security", body: "Password, active sessions, and closing your account.",
        tid: "account-card-security",
      },
      {
        to: "/account/preferences", icon: SwatchBook, tint: "hsl(24 92% 96%)", iconColor: "hsl(20 85% 48%)",
        title: "Preferences", body: "How Klareet looks on this device.",
        tid: "account-card-preferences",
      },
    ],
  },
  {
    label: "help",
    items: [
      {
        to: "/support", icon: LifeBuoy, tint: "hsl(330 70% 96%)", iconColor: "hsl(330 60% 55%)",
        title: "Support", body: "Stuck on something? Get help from a real person.",
        tid: "account-card-support",
      },
      {
        to: "/whats-new", icon: Sparkles, tint: "hsl(203 60% 95%)", iconColor: "hsl(205 60% 45%)",
        title: "What's new", body: "What changed in Klareet lately, and what is coming.",
        tid: "account-card-whatsnew",
      },
    ],
  },
];

export default function AccountHubScreen() {
  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24" data-testid="account-hub">
      <div className="k-label mb-2">account</div>
      <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="account-hub-title">
        Your account.
      </h1>
      <p className="k-hero-sub mb-8">Everything about you and your Klareet, in one calm place.</p>

      {GROUPS.map((g) => (
        <div key={g.label} className="mb-7" data-testid={`account-group-${g.label}`}>
          <div className="k-label mb-3">{g.label}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {g.items.map((it) => {
              const Icon = it.icon;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  className="k-card k-lift !p-4 flex items-start gap-3.5"
                  data-testid={it.tid}
                >
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: it.tint, color: it.iconColor }}>
                    <Icon size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold block" style={{ color: "hsl(var(--klareet-ink))" }}>{it.title}</span>
                    <span className="k-quiet !text-xs block mt-0.5" style={{ lineHeight: 1.5 }}>{it.body}</span>
                  </span>
                  <ChevronRight size={16} className="shrink-0 mt-2.5" style={{ color: "hsl(var(--klareet-muted))" }} />
                </Link>
              );
            })}
          </div>
        </div>
      ))}

      <p className="k-quiet !text-xs mt-2">
        Your answers and your read belong to you. Colleges and companies only ever see anonymous group numbers, never you.
      </p>
    </div>
  );
}
